import type { ArenaDefinition, ArenaGameplayDefinition, CardDefinition, PlayerId, Rect, WorldPoint } from './types';

export const ARENA_EDGE_INSET = 300;

export function clampToBounds(value: number, origin: number, length: number, inset = 0): number {
  return Math.max(origin + inset, Math.min(origin + length - inset, Math.trunc(value)));
}

export function clampPoint(point: WorldPoint, bounds: Rect, inset = 0): WorldPoint {
  return {
    x: clampToBounds(point.x, bounds.x, bounds.width, inset),
    y: clampToBounds(point.y, bounds.y, bounds.height, inset),
  };
}

export function isPointInsideBounds(point: WorldPoint, bounds: Rect, inset = 0): boolean {
  return point.x >= bounds.x + inset && point.x <= bounds.x + bounds.width - inset
    && point.y >= bounds.y + inset && point.y <= bounds.y + bounds.height - inset;
}

export function laneForX(gameplay: ArenaGameplayDefinition, x: number): 'left' | 'right' {
  return x < gameplay.worldBounds.x + gameplay.worldBounds.width / 2 ? 'left' : 'right';
}

export function isOnOwnSide(gameplay: ArenaGameplayDefinition, playerId: PlayerId, point: WorldPoint): boolean {
  return playerId === 'player' ? point.y >= gameplay.playerDeployMinY : point.y <= gameplay.botDeployMaxY;
}

export function isOnEnemySide(gameplay: ArenaGameplayDefinition, playerId: PlayerId, point: WorldPoint): boolean {
  return playerId === 'player' ? point.y <= gameplay.botDeployMaxY : point.y >= gameplay.playerDeployMinY;
}

export function basicPlacementValid(gameplay: ArenaGameplayDefinition, playerId: PlayerId, card: CardDefinition, point: WorldPoint): boolean {
  if (!isPointInsideBounds(point, gameplay.worldBounds, ARENA_EDGE_INSET)) return false;
  return card.placement === 'anywhere' || isOnOwnSide(gameplay, playerId, point);
}

export function projectileOrigin(gameplay: ArenaGameplayDefinition, owner: PlayerId): WorldPoint {
  const bounds = gameplay.worldBounds;
  return { x: bounds.x + bounds.width / 2, y: owner === 'player' ? bounds.y + bounds.height : bounds.y };
}

export function sceneSize(arena: ArenaDefinition): { width: number; height: number } {
  return { width: arena.presentation.camera.width / 10, height: arena.presentation.camera.height / 10 };
}

export function worldToScene(arena: ArenaDefinition, point: WorldPoint): WorldPoint {
  const bounds = arena.gameplay.worldBounds;
  const size = sceneSize(arena);
  return {
    x: ((point.x - bounds.x) / bounds.width) * size.width,
    y: ((point.y - bounds.y) / bounds.height) * size.height,
  };
}

export function sceneToWorld(arena: ArenaDefinition, point: WorldPoint): WorldPoint {
  const bounds = arena.gameplay.worldBounds;
  const size = sceneSize(arena);
  return {
    x: Math.round(bounds.x + (point.x / size.width) * bounds.width),
    y: Math.round(bounds.y + (point.y / size.height) * bounds.height),
  };
}
